//
// ============================================================================
// IMPORTS
// ============================================================================
//

import type { FqlFilter } from "@/services/api/domains/visits/visits.types";

//
// ============================================================================
// CONSTANTS
// ============================================================================
//

/**
 * FQL operator options — string-match operators + equality.
 * Displayed labels are resolved via i18n in the filter dialog.
 */
export const FQL_OPERATORS = [
  { value: "cn", labelKey: "pages.all-visits.filters.operators.cn" },
  { value: "sw", labelKey: "pages.all-visits.filters.operators.sw" },
  { value: "ew", labelKey: "pages.all-visits.filters.operators.ew" },
  { value: "eq", labelKey: "pages.all-visits.filters.operators.eq" },
] as const;

/** Field type determines which operators and value inputs are available. */
export type FqlFieldType = "string" | "boolean" | "date" | "numeric";

/** Fields available for FQL filtering with their i18n label keys. */
export const FQL_FIELDS = [
  {
    value: "patient.givenName",
    labelKey: "pages.all-visits.filters.fields.givenName",
    hintKey: "pages.all-visits.filters.fieldHints.givenName",
    type: "string" as FqlFieldType,
  },
  {
    value: "patient.middleName",
    labelKey: "pages.all-visits.filters.fields.middleName",
    hintKey: "pages.all-visits.filters.fieldHints.middleName",
    type: "string" as FqlFieldType,
  },
  {
    value: "patient.familyName",
    labelKey: "pages.all-visits.filters.fields.familyName",
    hintKey: "pages.all-visits.filters.fieldHints.familyName",
    type: "string" as FqlFieldType,
  },
  {
    value: "patient.patientId",
    labelKey: "pages.all-visits.filters.fields.patientId",
    hintKey: "pages.all-visits.filters.fieldHints.patientId",
    type: "numeric" as FqlFieldType,
  },
  {
    value: "patient.isMale",
    labelKey: "pages.all-visits.filters.fields.gender",
    hintKey: "pages.all-visits.filters.fieldHints.gender",
    type: "boolean" as FqlFieldType,
  },
  {
    value: "patient.dateOfBirth",
    labelKey: "pages.all-visits.filters.fields.dateOfBirth",
    hintKey: "pages.all-visits.filters.fieldHints.dateOfBirth",
    type: "date" as FqlFieldType,
  },
  {
    value: "date",
    labelKey: "pages.all-visits.filters.fields.date",
    hintKey: "pages.all-visits.filters.fieldHints.date",
    type: "date" as FqlFieldType,
  },
] as const;

/** Set of field values that are boolean type — used by the dialog to lock operator/value. */
export const BOOLEAN_FIELDS: Set<string> = new Set(
  FQL_FIELDS.filter((f) => f.type === "boolean").map((f) => f.value),
);

/** Set of field values that are date type — used by the dialog to lock operator to eq. */
export const DATE_FIELDS: Set<string> = new Set(
  FQL_FIELDS.filter((f) => f.type === "date").map((f) => f.value),
);

/** Set of field values that are numeric type — used by the dialog to restrict input to digits only. */
export const NUMERIC_FIELDS: Set<string> = new Set(
  FQL_FIELDS.filter((f) => f.type === "numeric").map((f) => f.value),
);

//
// ============================================================================
// HELPERS
// ============================================================================
//

/**
 * Compile an array of FqlFilter rows into an FQL query string following the
 * API's parenthesised format: `(field,operator,'value')` for strings,
 * `(field,operator,value)` for numbers and booleans (no escape characters).
 * Multiple conditions are joined with `and` (no spaces, as per spec).
 * Rows with empty field, operator, or value are skipped.
 */
export function compileFql(filters: FqlFilter[]): string {
  return filters
    .filter((f) => f.field.trim() && f.operator && f.value.trim())
    .map((f) => {
      const v = f.value.trim();
      const isNumeric = !isNaN(Number(v)) && v !== "";
      const isBool = v === "true" || v === "false";
      // Strings use single quotes (escape characters enabled per spec)
      const val = isNumeric || isBool ? v : `'${v}'`;
      return `(${f.field},${f.operator},${val})`;
    })
    .join("and");
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * FQL Filter State Composable
 *
 * Manages the FQL filter builder state for the all-visits page.
 *
 * Responsibilities:
 * - Maintain a tab-scoped UUID so each browser tab gets its own filter set
 * - Persist filter rows to localStorage keyed by the instance UUID
 * - Compile filter rows into an FQL query string
 * - Sync the compiled FQL to the URL `filter` query param
 * - Expose add / remove / clear / apply actions
 */
export const useFqlFilters = () => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const route = useRoute();
  const router = useRouter();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  /**
   * Live filter rows — shared reactively across ALL useFqlFilters() callers in
   * the same browser tab via useSessionState (useState + sessionStorage).
   * Each browser tab has its own sessionStorage, so tabs are naturally isolated.
   */
  const filters = useSessionState<FqlFilter[]>("all-visits.filters", []);

  /** Whether the filter dialog is open */
  const isFilterDialogOpen = useState<boolean>("all-visits.isFilterDialogOpen", () => false);

  /**
   * The compiled FQL string that was last applied.
   * Uses useState so all callers share the SAME reactive ref.
   */
  const compiledFql = useState<string>(
    "all-visits.compiledFql",
    () => (route.query.filter as string) ?? "",
  );

  /**
   * Snapshot of filter rows at the time Apply was last clicked.
   * Chips are derived from this — they only appear AFTER Apply, never
   * while a row is merely being built in the dialog.
   */
  const appliedFilters = useSessionState<FqlFilter[]>("all-visits.appliedFilters", []);

  //
  // ============================================================================
  // COMPUTED
  // ============================================================================
  //

  /**
   * Number of APPLIED filter conditions.
   * Derived from the appliedFilters snapshot — always in sync with what the
   * user last applied.
   */
  const filterCount = computed(() => appliedFilters.value.length);

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Add a new empty filter row to the end of the builder.
   */
  function addFilter(): void {
    filters.value = [
      ...filters.value,
      { id: crypto.randomUUID(), field: "", operator: "cn", value: "" },
    ];
  }

  /**
   * Add a new empty filter row immediately after the row with the given ID.
   * Falls back to appending at the end if the ID is not found.
   * @param afterId - UUID of the row after which the new row should be inserted
   */
  function addFilterAfter(afterId: string): void {
    const idx = filters.value.findIndex((f) => f.id === afterId);
    const newRow: FqlFilter = { id: crypto.randomUUID(), field: "", operator: "cn", value: "" };

    if (idx === -1) {
      filters.value = [...filters.value, newRow];
    } else {
      const copy = [...filters.value];
      copy.splice(idx + 1, 0, newRow);
      filters.value = copy;
    }
  }

  /**
   * Remove a filter row by its UUID.
   *
   * When the filter dialog is **closed** (chip removal), the change is
   * applied immediately so the table re-fetches without user action.
   *
   * When the dialog is **open** (row removal inside the builder), only the
   * working copy (`filters`) is updated — the user must click Apply to
   * commit changes to `appliedFilters` / `compiledFql` / URL.
   *
   * @param id - The UUID of the filter row to remove
   */
  function removeFilter(id: string): void {
    filters.value = filters.value.filter((f) => f.id !== id);

    // If the dialog is open the user is still editing — defer to Apply.
    if (isFilterDialogOpen.value) return;

    // Dialog is closed (chip removal) → apply immediately.
    appliedFilters.value = appliedFilters.value.filter((f) => f.id !== id);
    const fql = compileFql(filters.value);
    compiledFql.value = fql;
    void router.replace({ query: { ...route.query, filter: fql || undefined } });
  }

  /**
   * Remove all filter rows, clear compiledFql and the URL filter param.
   */
  function clearFilters(): void {
    filters.value = [];
    appliedFilters.value = [];
    compiledFql.value = "";
    void router.replace({ query: { ...route.query, filter: undefined } });
    isFilterDialogOpen.value = false;
  }

  /**
   * Compile the current filter rows into FQL, update compiledFql, and sync
   * the compiled expression to the URL `filter` query param.
   * Closes the dialog.
   */
  function applyFilters(): void {
    const fql = compileFql(filters.value);
    compiledFql.value = fql;
    // Snapshot only the rows that actually produce a filter clause
    appliedFilters.value = filters.value
      .filter((f) => f.field.trim() && f.operator && f.value.trim())
      .map((f) => ({ ...f }));
    void router.replace({
      query: { ...route.query, filter: fql || undefined },
    });
    isFilterDialogOpen.value = false;
  }

  /**
   * Update a single field on an existing filter row (used by in-place editing).
   * @param id - UUID of the row to update
   * @param patch - Partial FqlFilter fields to merge
   */
  function updateFilter(id: string, patch: Partial<Omit<FqlFilter, "id">>): void {
    const idx = filters.value.findIndex((f) => f.id === id);
    if (idx === -1) return;
    filters.value[idx] = { ...filters.value[idx], ...patch } as FqlFilter;
  }

  /**
   * Replace all filter rows with a new set (used by the dialog to commit a draft).
   * @param newFilters - The new filter rows to set
   */
  function setFilters(newFilters: FqlFilter[]): void {
    filters.value = newFilters;
  }

  /** Open the filter builder dialog */
  function openFilterDialog(): void {
    isFilterDialogOpen.value = true;
  }

  /** Close the filter builder dialog without applying */
  function closeFilterDialog(): void {
    isFilterDialogOpen.value = false;
  }

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    filters: readonly(filters),
    appliedFilters: readonly(appliedFilters),
    compiledFql: readonly(compiledFql),
    filterCount,
    isFilterDialogOpen: readonly(isFilterDialogOpen),

    addFilter,
    addFilterAfter,
    removeFilter,
    clearFilters,
    applyFilters,
    setFilters,
    updateFilter,
    openFilterDialog,
    closeFilterDialog,
  };
};
